import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LOW_CREDIT_THRESHOLD } from '../config/constants';

const BASE_URL = '';

// Global alert that warns advertisers when their available credit balance
// falls at or below LOW_CREDIT_THRESHOLD. Rendered once in DashboardLayout so
// it stays visible across every dashboard page. The balance is refetched on
// each route change so it reflects activity such as buying credits or
// launching a campaign without a full page reload.
const LowCreditBanner = () => {
  const { token } = useAuth();
  const location = useLocation();
  const [balance, setBalance] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchBalance = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BASE_URL}/api/payments/balance`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBalance(data.balance?.current ?? null);
      }
    } catch (error) {
      // Fail silently — the banner is a non-critical enhancement and should
      // never block the rest of the dashboard.
      console.error('Error fetching credit balance for low-credit alert:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance, location.pathname]);

  const isLow = balance !== null && balance <= LOW_CREDIT_THRESHOLD;

  // Re-show the alert whenever the balance drops back into the low range, even
  // if it was previously dismissed in this session.
  useEffect(() => {
    if (isLow) setDismissed(false);
  }, [isLow]);

  if (!isLow || dismissed) return null;

  const isEmpty = balance <= 0;

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-3">
        <div className="flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
          <div className="flex items-start sm:items-center gap-3">
            <svg
              className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5 sm:mt-0"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-amber-800">
              <span className="font-semibold">
                {isEmpty ? 'You are out of advertising credits.' : 'Low credit balance.'}
              </span>{' '}
              {isEmpty
                ? 'Your campaigns may stop running. '
                : `Your available balance is $${Number(balance).toFixed(2)}, which is at or below the $${LOW_CREDIT_THRESHOLD.toFixed(2)} threshold. `}
              Top up to keep your campaigns running.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/dashboard/payments"
              className="inline-flex items-center bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium py-1.5 px-3 rounded-lg transition-colors duration-200"
            >
              Buy Credits
            </Link>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              className="p-1 text-amber-500 hover:text-amber-700 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400"
              aria-label="Dismiss low credit alert"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LowCreditBanner;
